/**
 * El cuestionario hablado — `POST /job-apply/api/screening/transcribe` y
 * `POST /job-apply/api/screening/coach`.
 *
 * Dos llamadas con contratos MUY distintos, y por eso dos formas de fallar
 * distintas:
 *
 * `transcribe` sí falla de verdad —el audio pesó de más, no se oyó nada, el
 * teléfono grabó en un contenedor raro, OpenAI se cayó— y cada caso tiene su
 * frase, porque la salida SIEMPRE es la misma: escribir. Ninguno de estos
 * errores puede leerse como "no se pudo, vuelve mañana".
 *
 * `coach` no falla nunca de cara a la persona. El servidor ya devuelve 200 con
 * un veredicto neutro cuando DeepSeek se cae o se acaba el cupo, y acá se hace
 * lo mismo con la red: un veredicto que no repregunta ni celebra. El coaching es
 * un extra que suma material; si se cae, el cuestionario sigue igual de válido.
 * Un `throw` acá sería un formulario trabado por una función de apoyo.
 */
import { messages } from '~/i18n';
import { apiFetch } from '~/lib/api';
import { ANSWER_QUALITIES, MAX_AUDIO_BYTES, extensionFor } from '../lib/voice';
import type { AnswerQuality, InputMode } from '../lib/voice';

export type { AnswerQuality, InputMode };

export interface CoachVerdict {
  quality: AnswerQuality;
  /** La repregunta curiosa. null = no hay nada más que sacar, o ya van dos. */
  follow_up: string | null;
  /** Sólo cuando la respuesta salió rica: la micro-celebración. */
  encouragement: string | null;
}

/** Lo que devuelve el coach cuando no hay coach: no repregunta, no celebra. */
export const NEUTRAL_VERDICT: CoachVerdict = {
  quality: 'ok',
  follow_up: null,
  encouragement: null,
};

export class TranscriptionError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, options: { code: string; status: number }) {
    super(message);
    this.name = 'TranscriptionError';
    this.code = options.code;
    this.status = options.status;
  }
}

/**
 * Cada frase termina ofreciendo la misma salida: escribirlo.
 *
 * No se dice "intenta de nuevo" a secas en ningún caso donde reintentar tenga
 * buena chance de volver a fallar (formato que el teléfono no sabe grabar, cupo
 * agotado, OpenAI caído): eso es mandar a alguien a chocar dos veces contra la
 * misma pared con el teclado abierto y la batería al 8%.
 */
function messageFor(code: string): string {
  // Se lee al fallar y no al importar: el idioma pudo cambiar desde entonces.
  const text: Record<string, string> = messages().screening.transcribeErrors;
  return text[code] ?? text.unknown;
}

/**
 * El audio a texto. `duration_seconds` viaja porque el servidor corta a los 90 s
 * con lo que DECLARA el cliente: el peso del blob no dice cuánto duró (opus a
 * 24 kbps hace 90 s en menos de 300 KB) y el tope de 8 MB no alcanza a frenar
 * una grabación larga en un códec liviano.
 */
export async function transcribeAnswer(audio: Blob, durationSeconds: number): Promise<string> {
  // El 413 se puede predecir sin subir nada: 8 MB por una red móvil son minutos.
  if (audio.size > MAX_AUDIO_BYTES) {
    throw new TranscriptionError(messageFor('audio_too_large'), {
      code: 'audio_too_large',
      status: 413,
    });
  }

  const form = new FormData();
  form.append('audio', audio, `answer.${extensionFor(audio.type)}`);
  form.append('duration_seconds', String(Math.round(durationSeconds)));

  let res: Response;
  try {
    // Sin `content-type`: lo pone el navegador CON el boundary del multipart.
    // Escribirlo a mano es la forma clásica de que multer no encuentre nada.
    res = await apiFetch('/job-apply/api/screening/transcribe', { method: 'POST', body: form });
  } catch {
    throw new TranscriptionError(messageFor('network'), { code: 'network', status: 0 });
  }

  const payload = (await res.json().catch(() => null)) as {
    text?: string;
    code?: string;
    error?: string;
  } | null;

  if (!res.ok) {
    const code = payload?.code ?? (res.status === 401 ? 'unauthorized' : 'unknown');
    throw new TranscriptionError(messageFor(code), { code, status: res.status });
  }

  const text = (payload?.text ?? '').trim();
  if (!text) {
    throw new TranscriptionError(messageFor('empty_audio'), { code: 'empty_audio', status: 200 });
  }
  return text;
}

export interface CoachRequest {
  field: string;
  question: string;
  answer: string;
  /** Cuántas repreguntas ya se hicieron en ESTE campo. El backend corta en 2. */
  priorFollowUps: number;
}

/**
 * El coach. Nunca lanza: la peor respuesta posible es un veredicto neutro.
 *
 * Los topes del contrato se aplican acá (80/400/4000) porque el schema del
 * servidor es estricto y devolvería un 400 por una respuesta larga —justo la
 * respuesta rica que queríamos premiar.
 */
export async function coachAnswer(input: CoachRequest): Promise<CoachVerdict> {
  try {
    const res = await apiFetch('/job-apply/api/screening/coach', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        field: input.field.slice(0, 80),
        question: input.question.slice(0, 400),
        answer: input.answer.slice(0, 4000),
        prior_follow_ups: Math.max(0, Math.min(10, Math.floor(input.priorFollowUps))),
      }),
    });

    if (!res.ok) return NEUTRAL_VERDICT;

    const payload = (await res.json().catch(() => null)) as Partial<CoachVerdict> | null;
    const quality = ANSWER_QUALITIES.includes(payload?.quality as AnswerQuality)
      ? (payload?.quality as AnswerQuality)
      : 'ok';

    return {
      quality,
      // El backend ya fuerza las reglas duras (rich ⇒ sin repregunta, dos y
      // basta), pero un `null` que llegue como texto o como cadena vacía tiene
      // que morir acá: una burbuja del coach vacía es peor que ninguna.
      follow_up: cleanPhrase(payload?.follow_up),
      encouragement: quality === 'rich' ? cleanPhrase(payload?.encouragement) : null,
    };
  } catch {
    return NEUTRAL_VERDICT;
  }
}

function cleanPhrase(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'null') return null;
  return trimmed;
}
